import WorkingFileUploader from '@/components/AdvancedUpload';
import BatchList from '@/components/BatchList';
import FileList from '@/components/FileList';
import { UploadInProgressNotice } from '@/components/UploadInProgressNotice';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { execute } from '@/graphql/execute';
import { ProcessJobType, Project } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSubscription } from '@/hooks/useSubscription';
import { useUpload } from '@/hooks/useUpload';
import PageSkeleton from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import { completeBatch, startBatch, startBatchProcessing } from '@/queries/attachment.query';
import { DangerTriangle, HardDrive } from '@mynaui/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FunctionComponent, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Link2, Loader2 } from 'lucide-react';
import BatchDetailsDrawer from '../Batch/BatchDetailsDrawer';
import { DataCenterLayout } from '../DataCenterLayout';

interface iindexProps {}

const formatBytes = (bytes: number, k: number = 1024): string => {
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const index: FunctionComponent<iindexProps> = () => {
  const {
    isUploading,
    isUploadComplete,
    uploadProgress,
    error: uploadError,
    cancelUpload,
    startUploads,
    files,
    addFiles,
    removeFile,
    clearFiles,
    storageInfo,
  } = useUpload({ showProgressToast: false });
  const { organization } = useOrganizations();
  const { initialLoadComplete } = useSubscription();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get('batch');
  const closeBatch = () =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('batch');
      return next;
    }, { replace: true });
  const queryClient = useQueryClient();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importType, setImportType] = useState<'WMS' | 'POTREE'>('WMS');
  const [batchLabel, setBatchLabel] = useState('');
  const { t } = useTranslation('project');

  const importMutation = useMutation({
    mutationFn: async (url: string) => {
      const batchResult = (await execute(startBatch, {
        project: projectId!,
        numberOfAttachments: 0,
      })) as any;
      const newBatchId = batchResult?.batch?.id;
      if (!newBatchId) throw new Error('Failed to create batch');

      await execute(completeBatch, { id: newBatchId });

      await execute(startBatchProcessing, {
        id: newBatchId,
        srid: 4326,
        type: importType as ProcessJobType,
        importUrl: url,
      });
    },
    onSuccess: () => {
      analytics.capture('Import Started', { type: importType });
      setShowImportDialog(false);
      setImportUrl('');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('upload.importStarted', { type: importType }));
    },
    onError: () => {
      toast.error(t('upload.failedImport', { type: importType }));
    },
  });

  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();

  usePageMeta({ title: [t('upload.title'), project?.name] });

  // Calculate E57 file limits
  const e57Warning = useMemo(() => {
    const e57Files = files.filter((f) => f.name.toLowerCase().endsWith('.e57'));
    const e57Count = e57Files.length;
    const e57TotalBytes = e57Files.reduce((sum, f) => sum + f.file.size, 0);
    const MAX_E57_COUNT = 32;
    const MAX_E57_BYTES = 100 * 1024 * 1024 * 1024; // 100 GB
    const exceedsCount = e57Count > MAX_E57_COUNT;
    const exceedsSize = e57TotalBytes > MAX_E57_BYTES;
    return { exceedsCount, exceedsSize, e57Count, e57TotalBytes, show: exceedsCount || exceedsSize };
  }, [files]);

  // Calculate storage status
  const storageStatus = useMemo(() => {
    const storageUsedBytes = organization?.storageUsedBytes ?? 0;
    const storageLimitBytes = storageInfo.storageLimitBytes;
    const pendingBytes = storageInfo.pendingUploadBytes;
    const totalAfterUpload = storageUsedBytes + pendingBytes;
    const wouldExceedLimit = totalAfterUpload > storageLimitBytes;
    const usagePercent = Math.min((storageUsedBytes / storageLimitBytes) * 100, 100);
    const projectedPercent = Math.min((totalAfterUpload / storageLimitBytes) * 100, 100);
    const remainingBytes = Math.max(storageLimitBytes - storageUsedBytes, 0);

    return {
      storageUsedBytes,
      storageLimitBytes,
      pendingBytes,
      totalAfterUpload,
      wouldExceedLimit,
      usagePercent,
      projectedPercent,
      remainingBytes,
    };
  }, [organization?.storageUsedBytes, storageInfo.storageLimitBytes, storageInfo.pendingUploadBytes]);

  return (
    <PageSkeleton
      steps={
        projectLoading || !project
          ? [{ label: t('edit.loading'), href }]
          : [
              { label: project.name, href },
              {
                label: t('upload.title'),
                href: `${href}/datacenter`,
              },
              {
                label: t('upload.tabLabel'),
                href: `${href}/datacenter`,
                options: [
                  { label: t('upload.tabLabel'), href: `${href}/datacenter` },
                  { label: t('analyses.tabLabel'), href: `${href}/datacenter/analyses` },
                ],
              },
            ]
      }
    >
      <DataCenterLayout href={href} activeTab='upload'>
        <div id='onboarding-upload' className='mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold tracking-tight'>{t('upload.uploadFiles')}</h2>
            <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>{t('upload.uploadDescription')}</p>
          </div>
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogTrigger asChild>
              <Button variant='outline' className='shrink-0'>
                <Link2 className='size-4' aria-hidden />
                {t('upload.importUrl')}
              </Button>
            </DialogTrigger>
            <DialogContent className='sm:max-w-md'>
              <DialogHeader>
                <DialogTitle>{t('upload.importUrl')}</DialogTitle>
                <DialogDescription>{t('upload.importDescription')}</DialogDescription>
              </DialogHeader>
              <form
                className='space-y-4'
                onSubmit={(event) => {
                  event.preventDefault();
                  if (importUrl.trim() && !importMutation.isPending) importMutation.mutate(importUrl.trim());
                }}
              >
                <div className='space-y-2'>
                  <Label htmlFor='import-type'>{t('upload.importType')}</Label>
                  <Select
                    value={importType}
                    onValueChange={(v) => setImportType(v as 'WMS' | 'POTREE')}
                    disabled={importMutation.isPending}
                  >
                    <SelectTrigger id='import-type' className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='WMS'>WMS</SelectItem>
                      <SelectItem value='POTREE'>Potree</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {importType === 'POTREE' && (
                  <Alert>
                    <DangerTriangle className='h-4 w-4' />
                    <AlertDescription>{t('upload.potreeWarning')}</AlertDescription>
                  </Alert>
                )}
                <div className='space-y-2'>
                  <Label htmlFor='import-url'>{t('upload.url')}</Label>
                  <Input
                    id='import-url'
                    type='url'
                    required
                    disabled={importMutation.isPending}
                    placeholder={
                      importType === 'POTREE' ? 'https://example.com/potree/metadata.json' : 'https://example.com/wms'
                    }
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                  />
                </div>
                <DialogFooter>
                  <Button type='button' variant='outline' onClick={() => setShowImportDialog(false)}>
                    {t('upload.cancel')}
                  </Button>
                  <Button type='submit' disabled={!importUrl.trim() || importMutation.isPending}>
                    {importMutation.isPending && <Loader2 className='size-4 animate-spin' aria-hidden />}
                    {t('upload.import')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Storage Usage Bar */}
        <div className='mb-6 rounded-lg border border-border/70 bg-muted/30 p-4'>
          <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <HardDrive className='size-4 text-muted-foreground' aria-hidden />
              <span className='text-sm font-medium'>{t('upload.storage')}</span>
            </div>
            {initialLoadComplete && storageStatus.remainingBytes > 0 && (
              <span className='text-xs tabular-nums text-muted-foreground'>
                {formatBytes(storageStatus.remainingBytes, 1000)} {t('upload.remaining')}
              </span>
            )}
          </div>
          {!initialLoadComplete ? (
            <div className='space-y-2'>
              <Skeleton className='h-2 w-full' />
              <div className='flex justify-between'>
                <Skeleton className='h-3 w-24' />
                <Skeleton className='h-3 w-20' />
              </div>
            </div>
          ) : (
            <>
              <Progress
                value={storageStatus.projectedPercent}
                role='progressbar'
                aria-label={t('upload.storage')}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={storageStatus.projectedPercent}
                className='mb-2 h-1.5'
              />
              <div className='flex flex-wrap justify-between gap-x-4 gap-y-1 text-xs tabular-nums text-muted-foreground'>
                <span>
                  {formatBytes(storageStatus.storageUsedBytes, 1000)} {t('upload.used')}
                  {storageStatus.pendingBytes > 0 && (
                    <span className='text-blue-500'>
                      {' '}
                      + {formatBytes(storageStatus.pendingBytes, 1000)} {t('upload.pending')}
                    </span>
                  )}
                </span>
                <span>
                  {formatBytes(storageStatus.storageLimitBytes, 1000)} {t('upload.limit')}
                </span>
              </div>
              {(organization as any)?.ephemeralBytes > 0 && (
                <p className='mt-1 text-xs text-muted-foreground'>
                  {t('upload.includingEphemeral', {
                    size: formatBytes((organization as any).ephemeralBytes, 1000),
                  })}
                </p>
              )}
            </>
          )}
        </div>

        {/* Storage Limit Warning */}
        {initialLoadComplete && storageStatus.wouldExceedLimit && files.length > 0 && (
          <Alert variant='destructive' className='mb-6'>
            <DangerTriangle className='h-4 w-4' />
            <AlertTitle>{t('upload.storageLimitExceeded')}</AlertTitle>
            <AlertDescription>
              {t('upload.storageLimitExceededDescription', {
                size: formatBytes(storageStatus.pendingBytes),
                remaining: formatBytes(storageStatus.remainingBytes),
              })}
            </AlertDescription>
          </Alert>
        )}

        {/* Near Limit Warning */}
        {initialLoadComplete && !storageStatus.wouldExceedLimit && storageStatus.usagePercent >= 90 && (
          <Alert className='mb-6'>
            <DangerTriangle className='h-4 w-4' />
            <AlertTitle>{t('upload.storageAlmostFull')}</AlertTitle>
            <AlertDescription>
              {t('upload.storageAlmostFullDescription', { percent: storageStatus.usagePercent.toFixed(0) })}
            </AlertDescription>
          </Alert>
        )}

        {/* E57 File Limit Warning */}
        {e57Warning.show && (
          <Alert className='mb-6'>
            <DangerTriangle className='h-4 w-4' />
            <AlertTitle>{t('upload.e57LimitWarningTitle')}</AlertTitle>
            <AlertDescription>
              {e57Warning.exceedsCount && e57Warning.exceedsSize
                ? t('upload.e57LimitWarningBoth', {
                    count: e57Warning.e57Count,
                    size: formatBytes(e57Warning.e57TotalBytes),
                  })
                : e57Warning.exceedsCount
                  ? t('upload.e57LimitWarningCount', { count: e57Warning.e57Count })
                  : t('upload.e57LimitWarningSize', { size: formatBytes(e57Warning.e57TotalBytes) })}
            </AlertDescription>
          </Alert>
        )}

        <WorkingFileUploader onFilesAdded={addFiles} />
        {files.length > 0 && (
          <>
            {files.length > 0 && !isUploading && (
              <div className='mt-4 flex flex-col gap-4 sm:flex-row sm:items-end'>
                <div className='w-full flex-1'>
                  <Label htmlFor='upload-batch-label'>{t('upload.batchLabel')}</Label>
                  <Input
                    id='upload-batch-label'
                    className='mt-1'
                    placeholder={t('upload.batchLabelPlaceholder')}
                    value={batchLabel}
                    onChange={(e) => setBatchLabel(e.target.value)}
                    maxLength={255}
                  />
                </div>
                <div className=''>
                  {!isUploading && files.length > 0 && (
                    <Button
                      onClick={() => startUploads({ label: batchLabel || undefined })}
                      disabled={!initialLoadComplete || storageStatus.wouldExceedLimit}
                    >
                      {!initialLoadComplete
                        ? t('edit.loading')
                        : storageStatus.wouldExceedLimit
                          ? t('upload.storageLimitExceeded')
                          : t('upload.startUpload')}
                    </Button>
                  )}
                  {isUploading && (
                    <Button variant='destructive' onClick={cancelUpload}>
                      {t('upload.cancelUpload')}
                    </Button>
                  )}
                </div>
              </div>
            )}
            {/* Stay-on-page notice, right above the per-file progress. Hidden on
                failure — the error toast owns that state. */}
            {isUploading && !uploadError && (
              <UploadInProgressNotice
                className='mt-4'
                phase={isUploadComplete ? 'complete' : 'uploading'}
                progress={uploadProgress}
              />
            )}
            <Label className='mb-2 mt-4'>{t('upload.uploadedItems', { count: files.length })}</Label>
            <FileList files={files} onRemoveFile={removeFile} onClearAll={clearFiles} />
            <div className='h-8' />
          </>
        )}

        <ErrorBoundary fallback={<div>{t('upload.error')}</div>}>
          <div id='onboarding-batch'>
            <BatchList />
          </div>
        </ErrorBoundary>
        <BatchDetailsDrawer batchId={batchId} project={project} href={href} onClose={closeBatch} />
      </DataCenterLayout>
    </PageSkeleton>
  );
};

export default index;
